import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { ERROR_PAGE_TYPES } from '@zg-rentals/zillow-rental-manager/constants';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import { useFeedListingIdRedirect } from '../../../../hooks';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import { getCreateListingRoute } from '../../../../constants/urls';

import { useAuth } from '../../../../components/AuthProvider';
import { CLPLoadingState } from '../../../../components/CreateListingPage/LoadingState';
import ErrorPage from '../../../../components/ErrorPage';
import AuthCheckWrapper from '../../../../containers/AuthCheckWrapper';
import CreateListingPage from '../../../../containers/CreateListingPage';
import PropertyDetailsContainer from '../../../../containers/PropertyDetailsContainer';

import { type DatarouterError } from '@zg-rentals/web-base';

const PropertyDetailsPage = () => {
  const router = useRouter();
  const listingAlias = router.isReady ? (router.query.listingAlias as string) : '';
  const { isAuthorized, user } = useAuth();

  useFeedListingIdRedirect(listingAlias);

  const {
    data: listingDetails,
    error,
    isPending,
  } = useGetListingDetails(listingAlias, {
    enabled: isAuthorized,
  });

  const canAccessModularListing = accessUtils.canAccessModularListing({
    isMultifamily: listingDetails?.isMultifamily ?? false,
    listingTypeCode: listingDetails?.listingType?.code ?? 0,
    lastActivatedMs: listingDetails?.lastActivatedMs,
    userCanAccessModularListing: user?.features?.canAccessModularListing ?? false,
  });

  const isStreetEasyRestricted = Boolean(listingDetails?.restrictions?.isStreetEasyLocationCreateListingRestricted);

  // SF Redirect Guard
  useEffect(() => {
    // Fail fast: guard redirecting to create-listing and render ErrorPage.
    if (isStreetEasyRestricted) {
      return;
    }

    if (user && listingDetails && canAccessModularListing) {
      {
        router.push(getCreateListingRoute({ listingAlias }));
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [listingDetails, listingAlias, user, canAccessModularListing, isStreetEasyRestricted]);

  const isInReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed,
    isMultifamily: listingDetails?.isMultifamily,
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  if (isStreetEasyRestricted) {
    return <ErrorPage type={ERROR_PAGE_TYPES.STREETEASY_RESTRICTED} listingAlias={listingAlias} />;
  }

  // Error states
  if (error) {
    const httpStatus = (error as DatarouterError).responseStatus;

    if (httpStatus === 400) {
      return <ErrorPage type={ERROR_PAGE_TYPES.NOT_FOUND} />;
    }

    if (httpStatus === 403) {
      return <ErrorPage type={ERROR_PAGE_TYPES.UNAUTHORIZED} />;
    }

    return <ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />;
  }

  const { formattedStreet } = formatAddress({
    street: listingDetails?.street,
  });

  if (isPending && isAuthorized) {
    return <CLPLoadingState isZram={user?.isEnterpriseCustomer} />;
  }

  return (
    <AuthCheckWrapper
      loading={<CLPLoadingState isZram={user?.isEnterpriseCustomer} />}
      redirecting={<CLPLoadingState isZram={user?.isEnterpriseCustomer} />}
    >
      <Head>
        <title>Edit {formattedStreet || 'listing'} | Zillow Rental Manager</title>
      </Head>
      {!isPending &&
        !canAccessModularListing &&
        (isInReactivationFlow ? <CreateListingPage /> : <PropertyDetailsContainer />)}
    </AuthCheckWrapper>
  );
};

export default PropertyDetailsPage;
